'use client';

import Box from '../../../atoms/Box/Box';
import Button from '../../../atoms/Button/Button';
import Pill from '../../../atoms/Pill/Pill';
import type { FilterState, TableColumn, FilterValue } from '../types';
import styles from './FilterPills.module.scss';

type FilterPillData = {
  columnKey: string;
  columnTitle: string;
  value: FilterValue;
  displayValue: string;
};

export type FilterPillsProps = {
  /** Current filter state */
  filterState: FilterState;
  /** Table columns configuration */
  columns: TableColumn[];
  /** Callback when filters change */
  onFilterChange: (filters: FilterState) => void;
  /** Theme variant */
  theme?: 'light' | 'dark';
  /** Whether to show the Clear All button */
  showClearAll?: boolean;
  /** Data test ID prefix */
  dataTestId?: string;
  /** i18n labels for localization */
  labels?: {
    /** Label for "Clear All" button */
    clearAll?: string;
  };
};

/**
 * Looks up the display label for a filter value from column options
 */
const getDisplayLabel = (value: string, column: TableColumn): string => {
  const filterOptions = column.filter?.options;
  if (!filterOptions) return value;

  const option = filterOptions.find((opt) => opt.value === value);
  return option?.label || value;
};

/** Strips a dateRange bound's time/timezone portion (e.g. "2026-10-05T00:00:00Z" -> "2026-10-05") */
const formatRangeBound = (boundValue: string | number, isDateRange: boolean): string | number =>
  isDateRange && typeof boundValue === 'string' ? boundValue.split('T')[0] : boundValue;

/** Display value for a multiselect filter — a count once more than 2 are selected, labels otherwise. */
const getMultiselectDisplayValue = (values: string[], column: TableColumn): string => {
  if (values.length > 2) return `${values.length} selected`;
  return values.map((v) => getDisplayLabel(v, column)).join(', ');
};

/** Display value for a number/date range filter, e.g. "from - to", "≥ from", or "≤ to". */
const getRangeDisplayValue = (
  value: { from?: string | number; to?: string | number },
  column: TableColumn
): string => {
  const isDateRange = column.filter?.type === 'dateRange';
  const from = value.from !== undefined ? formatRangeBound(value.from, isDateRange) : undefined;
  const to = value.to !== undefined ? formatRangeBound(value.to, isDateRange) : undefined;

  if (from !== undefined && to !== undefined) return `${from} - ${to}`;
  if (from !== undefined) return `≥ ${from}`;
  if (to !== undefined) return `≤ ${to}`;
  return '';
};

/** Resolves the pill display text for a single filter value, or '' when there's nothing to show. */
const getDisplayValue = (value: FilterValue, column: TableColumn): string => {
  if (typeof value === 'string') return getDisplayLabel(value, column);
  if (Array.isArray(value)) return value.length ? getMultiselectDisplayValue(value, column) : '';
  if (typeof value === 'object' && value !== null) return getRangeDisplayValue(value, column);
  return '';
};

/**
 * Generates filter pill data from filter state
 */
const getFilterPills = (filterState: FilterState, columns: TableColumn[]): FilterPillData[] => {
  const pills: FilterPillData[] = [];

  Object.entries(filterState).forEach(([columnKey, value]) => {
    if (value === null || value === undefined) return;

    const column = columns.find((col) => col.key === columnKey);
    if (!column) return;

    const displayValue = getDisplayValue(value, column);

    if (displayValue) {
      pills.push({
        columnKey,
        columnTitle: column.title,
        value,
        displayValue,
      });
    }
  });

  return pills;
};

/**
 * Displays active filter pills with option to remove individual filters or clear all
 * @component
 */
const FilterPills = ({
  filterState,
  columns,
  onFilterChange,
  theme = 'dark',
  showClearAll = true,
  dataTestId = 'filter-pills',
  labels,
}: FilterPillsProps) => {
  const pills = getFilterPills(filterState, columns);

  if (pills.length === 0) return null;

  const handleRemoveFilter = (columnKey: string) => {
    const newFilterState = { ...filterState };
    delete newFilterState[columnKey];
    onFilterChange(newFilterState);
  };

  const handleClearAll = () => {
    onFilterChange({});
  };

  return (
    <Box className={styles.filterPillsRow} data-testid={dataTestId}>
      {showClearAll && (
        <Button
          size="small"
          label={labels?.clearAll ?? 'Clear All'}
          gaComponent="filter-pills-clear-all"
          dataTestId={`${dataTestId}-clear-all`}
          handleClick={handleClearAll}
          theme={theme}
          type="tertiary"
          color="neutral"
        />
      )}
      <Box className={styles.filterPillsContainer}>
        {pills.map((pill) => (
          <Pill
            key={pill.columnKey}
            label={`${pill.columnTitle}: ${pill.displayValue}`}
            theme={theme}
            color="neutral"
            type="secondary"
            size="small"
            icon={{
              category: 'global',
              name: 'close',
              size: 12,
            }}
            handleClick={() => handleRemoveFilter(pill.columnKey)}
            dataTestId={`${dataTestId}-${pill.columnKey}`}
          />
        ))}
      </Box>
    </Box>
  );
};

FilterPills.displayName = 'FilterPills';

export default FilterPills;
